import { useEffect, useState } from 'react'
import { ChevronLeft, Download } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

// Сколько живёт initData, переданная query-параметром. Должно совпадать с
// _INIT_DATA_TTL_QUERY на бэкенде (app/api/auth.py): такой URL уходит во
// внешний браузер и оседает в логах, поэтому срок короткий.
const QUERY_INIT_DATA_TTL_SEC = 15 * 60

/** Возраст initData в секундах, или null если auth_date не разобрать. */
function initDataAgeSec(initData: string): number | null {
  const authDate = new URLSearchParams(initData).get('auth_date')
  if (!authDate) return null
  const issuedAt = Number(authDate)
  if (!Number.isFinite(issuedAt)) return null
  return Date.now() / 1000 - issuedAt
}

export function Export() {
  const location = useLocation()
  const cameFromSettings =
    (location.state as { from?: string } | null)?.from === '/settings'
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)

  useEffect(() => {
    fetch(`${BASE_URL}/api/export`, {
      headers: { 'X-Telegram-Init-Data': WebApp.initData },
    })
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`)
        return res.text()
      })
      .then(setHtml)
      .catch(err => setError(err.message))
  }, [])

  // Скачивание открываем во внешнем браузере: внутри Telegram Mini App
  // программный клик по ссылке не запускает загрузку. openLink передаёт
  // initData query-параметром, т.к. заголовки во внешний браузер не уходят.
  //
  // У такой ссылки короткий срок годности (15 мин), а WebApp.initData не
  // обновляется, пока приложение открыто. Поэтому проверяем возраст здесь:
  // иначе во внешнем браузере пользователь увидел бы сырой JSON с 401.
  const handleDownload = () => {
    const age = initDataAgeSec(WebApp.initData)
    if (age !== null && age > QUERY_INIT_DATA_TTL_SEC) {
      setStale(true)
      return
    }
    setStale(false)
    WebApp.HapticFeedback?.impactOccurred('light')
    const url = `${BASE_URL}/api/export?init_data=${encodeURIComponent(WebApp.initData)}&download=1`
    WebApp.openLink(url)
  }

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        {/* Экран открывается из двух мест — из профиля и из настроек, —
            поэтому «назад» ведёт туда, откуда пришли: откуда именно, сообщает
            сама ссылка через state. Без этого из настроек выбрасывало в
            профиль. */}
        <Link
          to={cameFromSettings ? '/settings' : '/profile'}
          className="btn-active inline-flex items-center gap-1 text-sm text-ink-1 mb-2"
        >
          <ChevronLeft size={16} /> {cameFromSettings ? 'Настройки' : 'Профиль'}
        </Link>
        <p className="text-h1 text-ink">Мои данные</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-4">
        <button
          onClick={handleDownload}
          className="btn-active w-full flex items-center justify-center gap-2 bg-accent text-white text-sm font-medium py-2.5 rounded-lg"
        >
          <Download size={16} />
          Скачать файлом
        </button>

        {stale && (
          <p className="text-sm text-ink-2">
            Ссылка на скачивание устарела. Закрой и открой приложение заново — и нажми ещё раз.
          </p>
        )}

        {error && (
          <p className="text-sm text-danger">Не удалось загрузить данные: {error}</p>
        )}

        {!error && !html && (
          <p className="text-sm text-ink-2 text-center py-8">Загрузка…</p>
        )}

        {html && (
          <iframe
            srcDoc={html}
            className="w-full rounded-xl border border-line-1"
            style={{ height: '70vh', display: 'block' }}
            title="Экспорт данных"
          />
        )}
      </div>
    </div>
  )
}
